import { useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Check, ShoppingBag } from "lucide-react";
import HP3_COLLECTIONS from "@/data/hp3Collections";

// HP3 — near-full-width, swipeable multi-collection product slider with
// category tabs. Real Riversol products per collection (data/hp3Collections).
// Softly-squared cards matching the HP3 system; drag/swipe on desktop + mobile
// plus prev/next arrows.
const Stars = ({ score, count }) => (
  <div className="inline-flex items-center gap-1.5 text-[12px] text-neutral-500">
    <span className="tracking-[0.2em] text-riversol-blue text-[11px]">★★★★★</span>
    <span className="tabular-nums text-neutral-600">{score.toFixed(1)}</span>
    <span className="text-neutral-400">({count.toLocaleString()})</span>
  </div>
);

const Atc = ({ testid }) => {
  const [added, setAdded] = useState(false);
  return (
    <button
      type="button"
      onClick={() => { if (!added) { setAdded(true); setTimeout(() => setAdded(false), 1500); } }}
      data-testid={testid}
      className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-[12px] tracking-[0.14em] uppercase font-medium transition-colors ${added ? "bg-riversol-blue/90 text-white" : "bg-riversol-blue text-white hover:bg-riversol-blueLight"}`}
    >
      {added ? <><Check size={15} strokeWidth={2} />Added</> : <><ShoppingBag size={14} strokeWidth={1.8} />Add to Cart</>}
    </button>
  );
};

const Card = ({ p }) => {
  const [sel, setSel] = useState(p.variants ? 0 : null);
  const badge = p.sale ? `−${p.sale}%` : p.badge;
  const vis = p.variants?.slice(0, 2) || [];
  const extra = (p.variants?.length || 0) - vis.length;
  return (
    <div data-vcard data-testid={`hp3-card-${p.id}`} className="group shrink-0 basis-[78%] sm:basis-[46%] lg:basis-[calc((100%-3*24px)/4)] flex flex-col border border-neutral-200/80 bg-white overflow-hidden select-none">
      <a href={p.href} className="relative block aspect-square bg-white" draggable={false}>
        <img src={p.image} alt={p.title} loading="lazy" draggable={false} className="absolute inset-0 w-full h-full object-contain p-6 transition-transform duration-500 group-hover:scale-[0.96]" />
        {badge && (
          <span className={`absolute top-3 right-3 font-mono text-[10px] tracking-[0.14em] uppercase px-2.5 py-1 tabular-nums ${p.sale ? "bg-riversol-blue text-white" : "bg-white text-neutral-900 border border-neutral-200"}`}>{badge}</span>
        )}
      </a>
      <div className="flex flex-col flex-1 p-5">
        <a href={p.href} className="text-[15px] font-medium leading-snug text-neutral-900 hover:text-riversol-blue transition-colors">{p.title}</a>
        <p className="mt-1 text-[13px] leading-[1.4] text-neutral-600 line-clamp-2">{p.desc}</p>
        <div className="mt-1.5"><Stars score={p.rating} count={p.reviews} /></div>
        <div className="flex-1 min-h-[10px]" />
        <div className="min-h-[42px] flex items-end">
          {p.variants ? (
            <div>
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500">{p.optionLabel}</span>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {vis.map((v, i) => (
                  <button key={v} type="button" onClick={() => setSel(i)} data-testid={`hp3-variant-${p.id}-${i}`}
                    className={`text-[11px] px-3 py-1.5 border transition-colors ${sel === i ? "bg-riversol-blue text-white border-riversol-blue" : "border-neutral-300 text-neutral-700 hover:border-riversol-blue hover:text-riversol-blue"}`}>{v}</button>
                ))}
                {extra > 0 && <span className="text-[11px] px-2.5 py-1.5 border border-neutral-300 text-neutral-500">+{extra}</span>}
              </div>
            </div>
          ) : (
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-400">One size</span>
          )}
        </div>
        <div className="mt-4 flex items-end gap-2">
          <span className="text-[15px] tabular-nums text-neutral-500 line-through">${p.price}</span>
          <span className="text-[22px] leading-none font-semibold tabular-nums text-riversol-blue">${p.memberPrice}</span>
          <span className="mb-0.5 inline-flex items-center bg-riversol-blue text-white font-mono text-[9px] tracking-[0.16em] uppercase px-2 py-[3px] leading-none">R+ Member</span>
        </div>
        <div className="mt-4"><Atc testid={`hp3-atc-${p.id}`} /></div>
      </div>
    </div>
  );
};

export const CollectionsSliderV3 = () => {
  const [tab, setTab] = useState(0);
  const trackRef = useRef(null);
  const drag = useRef({ down: false, x: 0, left: 0, moved: false });
  const collection = HP3_COLLECTIONS[tab];

  const step = () => {
    const el = trackRef.current;
    const c = el?.querySelector("[data-vcard]");
    return c ? c.getBoundingClientRect().width + 24 : (el?.clientWidth || 0) * 0.25;
  };
  const nudge = (d) => trackRef.current?.scrollBy({ left: d * step() * 1.5, behavior: "smooth" });
  const down = (e) => { const el = trackRef.current; drag.current = { down: true, x: e.clientX, left: el.scrollLeft, moved: false, pid: e.pointerId }; };
  const move = (e) => {
    if (!drag.current.down) return;
    const el = trackRef.current; const dx = e.clientX - drag.current.x;
    if (Math.abs(dx) > 6) { drag.current.moved = true; el.setPointerCapture?.(drag.current.pid); el.scrollLeft = drag.current.left - dx; }
  };
  const up = () => { drag.current.down = false; };
  const clickCap = (e) => { if (drag.current.moved) { e.preventDefault(); e.stopPropagation(); drag.current.moved = false; } };

  return (
    <section className="bg-riversol-paper py-16 lg:py-20" data-testid="home-collections-slider-v3">
      <div className="max-w-[1800px] mx-auto px-5 lg:px-10">
        {/* Tabs + arrows */}
        <div className="flex items-center justify-between gap-6 mb-8 lg:mb-10">
          <nav className="flex items-center gap-5 lg:gap-8 overflow-x-auto hide-scrollbar" style={{ scrollbarWidth: "none" }} data-testid="hp3-collection-tabs">
            {HP3_COLLECTIONS.map((c, i) => (
              <button key={c.key} type="button" onClick={() => { setTab(i); trackRef.current?.scrollTo({ left: 0 }); }} data-testid={`hp3-tab-${c.key}`}
                className={`relative shrink-0 pb-1.5 text-[15px] lg:text-[18px] tracking-[-0.01em] transition-colors ${i === tab ? "text-neutral-900" : "text-neutral-400 hover:text-neutral-700"}`}>
                {c.label}
                {i === tab && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-neutral-900" />}
              </button>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <button type="button" onClick={() => nudge(-1)} aria-label="Previous" data-testid="hp3-slider-prev" className="w-11 h-11 flex items-center justify-center bg-riversol-blue text-white hover:bg-riversol-blueLight transition-colors"><ArrowLeft size={16} /></button>
            <button type="button" onClick={() => nudge(1)} aria-label="Next" data-testid="hp3-slider-next" className="w-11 h-11 flex items-center justify-center bg-riversol-blue text-white hover:bg-riversol-blueLight transition-colors"><ArrowRight size={16} /></button>
          </div>
        </div>

        {/* Track */}
        <div ref={trackRef} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up} onClickCapture={clickCap}
          className="flex gap-6 overflow-x-auto hide-scrollbar cursor-grab active:cursor-grabbing pb-2" style={{ scrollbarWidth: "none" }} data-testid="hp3-slider-track">
          {collection.products.map((p) => <Card key={p.id} p={p} />)}
        </div>
      </div>
    </section>
  );
};

export default CollectionsSliderV3;
